import { apiClient } from "@/lib/apiClient";
import { storageService } from "@/services/storageService";

/**
 * Attempts to find the correct file path from the database via OVH API
 * 
 * NOTE: As of the latest implementation, file paths use document IDs for storage:
 * Format: {caseId}/{documentId}.{ext}
 * 
 * This function provides backward compatibility and handles edge cases where the backend
 * might still provide incorrect paths or use legacy filename-based paths.
 */
async function findCorrectFilePath(
  caseId: string,
  documentName: string,
  docId?: string
): Promise<string | null> {
  console.log('🔎 [findCorrectFilePath] Starting search with:', { caseId, documentName, docId });

  try {
    // Call OVH API to get documents for this case
    console.log('📡 [findCorrectFilePath] Calling API: GET /cases/' + caseId + '/documents');
    const documents = await apiClient.get<any[]>(`/cases/${caseId}/documents`);
    console.log('📡 [findCorrectFilePath] API Response - Total documents:', documents?.length || 0);

    if (!documents || documents.length === 0) {
      console.warn('⚠️ [findCorrectFilePath] No documents found in case');
      return null;
    }

    // Priority 1: Match by doc_id (UUID, 100% reliable)
    if (docId) {
      const byId = documents.find(doc => doc.id === docId);
      if (byId?.file_path) {
        console.log('✅ [findCorrectFilePath] Found by doc_id:', byId.file_path);
        return byId.file_path;
      }
      console.warn('⚠️ [findCorrectFilePath] doc_id provided but no match found:', docId);
    }

    // Priority 2: Fallback by document name (exact match)
    const candidates = documents.filter(doc => doc.name === documentName);
    console.log('🎯 [findCorrectFilePath] Filtering by name "' + documentName + '" - Found:', candidates.length);

    if (candidates.length === 0) {
      console.warn('⚠️ [findCorrectFilePath] No matches found for document name');
      console.log('📋 [findCorrectFilePath] Available document names:', documents.map(d => d.name).slice(0, 10));
      return null;
    }

    // Try each candidate to see if file actually exists in storage
    for (let i = 0; i < candidates.length; i++) {
      const doc = candidates[i];

      if (!doc.file_path) {
        continue;
      }

      try {
        await storageService.getSignedUrl(doc.file_path, { expiresIn: 60 });
        console.log('✅ [findCorrectFilePath] Found valid file:', doc.file_path);
        return doc.file_path;
      } catch (testError) {
        console.warn(`❌ [findCorrectFilePath] Candidate ${i + 1} failed storage test:`, testError);
        continue;
      }
    }

    return null;
  } catch (error) {
    console.error('[findCorrectFilePath] Error:', error);
    return null;
  }
}

/**
 * Chemin de stockage à jour d'un document, résolu par son identifiant seul.
 *
 * Contrairement à {@link getValidatedFilePath}, aucun repli par nom : un nom
 * n'identifie pas un fichier (deux pièces peuvent le partager, et un nom de
 * téléchargement « Pièce n°3 - Contrat.pdf » peut être celui d'une autre pièce
 * redéposée). Renvoie `null` si le document est absent du dossier ou si la
 * liste ne peut pas être lue : l'appelant garde alors le chemin qu'il détient.
 *
 * @param caseId - Dossier auquel appartient le document
 * @param documentId - `case_documents.id` de la pièce
 * @returns `file_path` du document, ou `null`
 */
export async function findFilePathByDocumentId(
  caseId: string,
  documentId: string,
): Promise<string | null> {
  try {
    const documents = await apiClient.get<Array<{ id: string; file_path?: string | null }>>(
      `/cases/${caseId}/documents`,
    );
    return documents?.find((doc) => doc.id === documentId)?.file_path || null;
  } catch (error) {
    console.warn(
      `[findFilePathByDocumentId] GET /cases/${caseId}/documents failed while resolving document ${documentId}; the caller keeps its own file_path.`,
      error,
    );
    return null;
  }
}

/**
 * Gets a secure signed URL for a document, with automatic path correction fallback
 * 
 * As of the latest implementation, all new documents use ID-based storage paths:
 * Format: {caseId}/{documentId}.{ext}
 * 
 * This function provides backward compatibility with legacy filename-based paths
 * and handles cases where the backend provides incorrect paths.
 * 
 * @param filePath - The file path from the backend (may be incorrect)
 * @param documentName - The document name for fallback lookup
 * @returns Object with signedUrl, corrected path if found, and whether path was corrected
 */
export async function getValidatedDocumentUrl(
  filePath: string,
  documentName: string
): Promise<{ 
  signedUrl: string | null; 
  correctedPath?: string;
  pathCorrected: boolean; 
  error?: string 
}> {
  try {
    // Clean the path
    const cleanPath = filePath.replace(/^\/+/, '');
    
    // First attempt: try the original path
    try {
      const signedUrl = await storageService.getSignedUrl(cleanPath, { expiresIn: 3600 });
      console.log('[getValidatedDocumentUrl] ✅ Original path works');
      return { 
        signedUrl, 
        pathCorrected: false 
      };
    } catch (error) {
      console.warn('[getValidatedDocumentUrl] ⚠️ Original path failed:', error);
      console.log('[getValidatedDocumentUrl] 🔍 Attempting to find correct path...');
    }
    
    // Second attempt: find the correct path from database
    const correctedPath = await findCorrectFilePath(cleanPath, documentName);
    
    if (!correctedPath) {
      return {
        signedUrl: null,
        pathCorrected: false,
        error: 'Document not found in storage or database'
      };
    }
    
    // Third attempt: try the corrected path
    try {
      const signedUrl = await storageService.getSignedUrl(correctedPath, { expiresIn: 3600 });
      console.log('[getValidatedDocumentUrl] ✅ Successfully corrected path');
      return {
        signedUrl,
        correctedPath: correctedPath,
        pathCorrected: true
      };
    } catch (correctedError) {
      return {
        signedUrl: null,
        pathCorrected: false,
        error: 'Corrected path validation failed'
      };
    }
    
  } catch (error) {
    console.error('[getValidatedDocumentUrl] Error:', error);
    return {
      signedUrl: null,
      pathCorrected: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * Validates a document path and returns the correct path if available
 * 
 * Works with both:
 * - New ID-based paths: {caseId}/{documentId}.{ext}
 * - Legacy filename-based paths: {caseId}/{timestamp}-{sanitizedName}.{ext}
 * 
 * @param filePath - The file path to validate
 * @param documentName - The document name for fallback lookup
 * @returns The correct file path (original or corrected)
 */
export async function getValidatedFilePath(
  filePath: string,
  documentName: string,
  caseId?: string,
  docId?: string
): Promise<string | null> {
  console.log('🔍 [getValidatedFilePath] Called with:', { filePath, documentName, caseId, docId });

  if (!caseId) {
    console.warn('⚠️ [getValidatedFilePath] No caseId provided, cannot validate path');
    return null;
  }

  try {
    const correctedPath = await findCorrectFilePath(caseId, documentName, docId);

    if (correctedPath) {
      console.log('✅ [getValidatedFilePath] Found correct path in DB:', correctedPath);
      return correctedPath;
    }

    console.warn('⚠️ [getValidatedFilePath] No valid path found in database');
    return null;
  } catch (error) {
    console.error('[getValidatedFilePath] Error:', error);
    return null;
  }
}

